/**
 * AgendaCalendarCard — card "Calendário conectado" da Agenda (issue #72).
 *
 * Três estados, como pede a issue:
 *   desconectado — botão "Conectar Google Calendar";
 *   conectado    — conta, último sync, "Sincronizar agora" e "Gerenciar";
 *   erro         — mensagem do vínculo (token revogado, por exemplo) + reconectar.
 *
 * O consent reusa o redirect_uri já registrado do Google Ads: gravamos o client
 * da agenda no sessionStorage e mandamos o usuário para o Google; a página
 * /integrations/google-ads/callback despacha de volta para cá pelo `provider`
 * assinado no state.
 *
 * Props:
 *   status       — { connected, google_email, calendar_id, last_synced_at, last_error, available }
 *   clientId     — client da agenda (vai para o sessionStorage antes do redirect)
 *   onChanged()  — conexão mudou (o pai recarrega status e eventos)
 *   onSynced(r)  — sincronização terminou (r = { criados, atualizados, removidos, enviados })
 */

import { useState } from 'react'
import Icon from '../../components/icons.jsx'
import { useApp } from '../../context/AppContext.jsx'
import { agCalendarAuthorize, agCalendarDisconnect, agCalendarSync } from '../../db/api.js'
import './agenda.css'

function fmtQuando(iso) {
  if (!iso) return null
  const d = new Date(iso)
  if (isNaN(d)) return null
  const hoje = new Date()
  const mesmoDia = d.toDateString() === hoje.toDateString()
  const hora = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  if (mesmoDia) return `hoje às ${hora}`
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} às ${hora}`
}

export default function AgendaCalendarCard({ status, clientId, onChanged, onSynced }) {
  const { confirm, toast } = useApp()
  const [busy, setBusy] = useState(null)   // 'conectar' | 'sync' | 'desconectar'
  const [err, setErr] = useState(null)

  // Migration 035 ainda não aplicada: o card some em vez de oferecer um botão
  // que só daria 503.
  if (status && status.available === false) return null

  const conectado = !!status?.connected
  const erro = status?.last_error || null
  const ultimoSync = fmtQuando(status?.last_synced_at)

  const conectar = async () => {
    if (busy) return
    setBusy('conectar'); setErr(null)
    try {
      sessionStorage.setItem('agenda_calendar_client_id', clientId)
      const { authorize_url: url } = await agCalendarAuthorize()
      window.location.href = url            // sai da SPA: consent do Google
    } catch (e) {
      setErr(e.message); setBusy(null)
    }
  }

  const sincronizar = async () => {
    if (busy) return
    setBusy('sync'); setErr(null)
    try {
      const r = await agCalendarSync()
      const total = (r.criados || 0) + (r.atualizados || 0) + (r.removidos || 0) + (r.enviados || 0)
      toast?.(
        total
          ? `Sincronizado: ${r.criados || 0} novos, ${r.atualizados || 0} atualizados, ${r.enviados || 0} enviados.`
          : 'Tudo já estava em dia.',
        'success'
      )
      onSynced?.(r)
    } catch (e) {
      setErr(e.message)
    } finally {
      setBusy(null)
    }
  }

  const desconectar = async () => {
    const ok = await confirm({
      title: 'Desconectar Google Calendar',
      message:
        'A sincronização para. Os agendamentos já criados continuam na agenda e no seu Google Calendar — nada é apagado.',
      confirmText: 'Desconectar',
      danger: true,
    })
    if (!ok) return
    setBusy('desconectar'); setErr(null)
    try {
      await agCalendarDisconnect()
      toast?.('Google Calendar desconectado.', 'success')
      onChanged?.()
    } catch (e) {
      setErr(e.message)
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="ag-card ag-panel">
      <div className="ag-panel-title">
        <Icon n="refresh" s={15} c="#12805c" /> Calendário conectado
      </div>

      {err && <div className="ag-inline-error">{err}</div>}

      {!conectado ? (
        <>
          <div className="ag-gcal-row">
            <span className="ag-gcal-logo"><Icon n="calendar" s={17} /></span>
            <div style={{ minWidth: 0 }}>
              <div className="ag-avail-label">Google Calendar</div>
              <div className="ag-avail-value">
                {erro ? 'Conexão interrompida' : 'Nenhuma conta conectada'}
              </div>
            </div>
          </div>
          {erro && <div className="ag-gcal-error">{erro}</div>}
          <button type="button" className="ag-ctrl primary ag-gcal-btn" onClick={conectar} disabled={busy}>
            <Icon n="link" s={14} />
            {busy === 'conectar' ? 'Abrindo o Google…' : (erro ? 'Reconectar' : 'Conectar Google Calendar')}
          </button>
        </>
      ) : (
        <>
          <div className="ag-gcal-row">
            <span className={`ag-gcal-logo${erro ? ' is-error' : ' is-ok'}`}>
              <Icon n={erro ? 'alert' : 'calendar'} s={17} />
            </span>
            <div style={{ minWidth: 0 }}>
              <div className="ag-avail-label">{status.google_email || 'Conta Google'}</div>
              <div className="ag-avail-value">
                {erro
                  ? 'Sincronização com erro'
                  : (ultimoSync ? `Sincronizado ${ultimoSync}` : 'Ainda não sincronizado')}
              </div>
            </div>
          </div>

          {erro && <div className="ag-gcal-error">{erro}</div>}

          <div className="ag-gcal-actions">
            <button type="button" className="ag-ctrl" onClick={sincronizar} disabled={busy}>
              <Icon n="refresh" s={13} />
              {busy === 'sync' ? 'Sincronizando…' : 'Sincronizar agora'}
            </button>
            <button type="button" className="ag-link" onClick={desconectar} disabled={busy}>
              {busy === 'desconectar' ? 'Desconectando…' : 'Gerenciar conexão'}
            </button>
          </div>
        </>
      )}
    </div>
  )
}
